import { Button, Checkbox, Form } from "@oxygen/core";
import SpeedometerLoader from "GlobalComponent/SpeedometerLoader";
import { makeCancelable } from "GlobalUtils/CancelablePromise";
import { validateEmail, validatePassword } from "GlobalUtils/Validate";
import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { logIn, setCurrentScreen } from "../actionCreator";
import CarwaleLoginApi from "../apis";
import { EmailInput, PasswordInput } from "../components/FormInputs";
import { SCREEN } from "../constants/Global";
import ContinueWithGoogle from "../containers/ContinueWithGoogle";
import { getUserInfo, validateInput } from "../utils";
import PropTypes from "prop-types";
import useInput from "../hooks/useInput";
import useReCaptcha from "../hooks/useReCaptcha";
import { RECAPTCHA_SITE_KEY } from "GlobalConstant/";
import ReCAPTCHA from "react-google-recaptcha";
import getThemeClasses from "@oxygen/theme";
import styles from "./scss/Login.module.scss";
import { CSSFlex } from "GlobalComponent/CSSCore";

const seperatorClasses = getThemeClasses(`
  vertical-align-middle;
  display-inline-block;
`);
const GradientSeperator = ({ isLeft }) => (
  <span
    className={`${seperatorClasses} ${styles.seperator} ${
      isLeft ? styles.leftGradient : styles.rightGradient
    }`}
  />
);

const propTypes = {
  /** Used to show toast on user actions */
  showToast: PropTypes.func.isRequired,
};
const loginButtonClasses = getThemeClasses(`
  mt-7;
`);
const keepSignedInClasses = getThemeClasses(`
  font-size-7.5;
  color-dimGray;
`);
const bottomContainerClasses = getThemeClasses(`
  flex-direction-column;
  align-items-center;
`);
const forgotPasswordClasses = getThemeClasses(`
  mt-5;
  color-blue-light;
  font-size-7.5;
  cursor-pointer;
`);
const seperatorContainerClasses = getThemeClasses(`
  mt-5;
  mb-5;
`);
const seperatorTextClasses = getThemeClasses(`
  ml-4;
  mr-4;
  font-size-8;
  color-dimGray;
`);
const signUpContainerClasses = getThemeClasses(`
  font-size-7.5;
  mt-5;
`);
const signUpClasses = getThemeClasses(`
  color-blue-light;
  ml-1;
  cursor-pointer;
  font-size-7.5;
  mt-5;
`);
/**
 * This component renders Login content and
 * Handles api calls and validations
 */
let apiPromise = null;

const Login = memo(function Login({ platForm, showToast }) {
  const dispatch = useDispatch();
  const [email, setEmail, onEmailChange] = useInput(validateEmail);
  const [password, setPassword, onPasswordChange] = useInput(validatePassword);
  const [rememberMe, setRememberMe] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { reCaptchaRef, triggerRecaptcha } = useReCaptcha();

  useEffect(() => {
    return () => {
      apiPromise && apiPromise.cancel();
    };
  }, []);

  function turnOnFetching() {
    setIsFetching(true);
  }
  function turnOffFetching() {
    setIsFetching(false);
  }
  const handleRememberMeChange = (event, isChecked) => {
    setRememberMe(isChecked);
  };
  function handleSignUpClick() {
    dispatch(setCurrentScreen(SCREEN.SIGN_UP));
  }
  function handleForgotPassword() {
    dispatch(setCurrentScreen(SCREEN.FORGOT_PASSWORD));
  }
  function handleLoginApiResponse(response) {
    turnOffFetching();
    if (response && response.status === 200) {
      dispatch(logIn(getUserInfo(response)));
      showToast("Login Successful");
    } else {
      showToast(response.message);
    }
  }
  function validateBeforeSubmit() {
    const inputValidation = validateInput({
      email: email.value,
      password: password.value,
    });
    const { isValid } = inputValidation;
    if (!isValid) {
      const { validation } = inputValidation;
      setEmail({ ...email, ...validation.email });
      setPassword({ ...password, ...validation.password });
    }
    return isValid;
  }
  function handleSubmit(event) {
    event.preventDefault();
    const isUserInputsValid = validateBeforeSubmit();
    if (!isUserInputsValid) {
      return;
    }
    triggerRecaptcha();
  }
  function handleLogin(reCaptchaResponse) {
    turnOnFetching();
    apiPromise && apiPromise.cancel();
    apiPromise = makeCancelable(
      CarwaleLoginApi.cwLogin({
        email: email.value,
        password: password.value,
        reCaptchaResponse,
        platForm,
        rememberMe,
      })
    );
    apiPromise.promise.then(handleLoginApiResponse).catch(error => {
      console.log("Error in Login api", error);
      showToast("Login failed");
      turnOffFetching();
    });
  }

  return (
    <>
      {isFetching && <SpeedometerLoader />}
      <Form>
        <EmailInput email={email} handleEmailInput={onEmailChange} />
        <PasswordInput
          password={password}
          isShowbutton
          handlePasswordInput={onPasswordChange}
        />
        <Checkbox
          block={false}
          size="sm"
          checked={rememberMe}
          onChange={handleRememberMeChange}
        >
          <span className={keepSignedInClasses}>Keep me signed in</span>
        </Checkbox>
        <ReCAPTCHA
          ref={reCaptchaRef}
          sitekey={RECAPTCHA_SITE_KEY}
          size="invisible"
          badge="bottomright"
          onChange={handleLogin}
        />
        <div className={styles.containerPadding}>
          <Button
            htmlType="submit"
            block
            onClick={handleSubmit}
            className={loginButtonClasses}
          >
            Login
          </Button>
        </div>
      </Form>
      <CSSFlex
        className={`${styles.containerPadding} ${bottomContainerClasses}`}
      >
        <span className={forgotPasswordClasses} onClick={handleForgotPassword}>
          Forgot Password?
        </span>
        <div className={seperatorContainerClasses}>
          <GradientSeperator />
          <span className={seperatorTextClasses}>Or</span>
          <GradientSeperator isLeft />
        </div>
        <ContinueWithGoogle
          onRequest={turnOnFetching}
          onCompletion={turnOffFetching}
          showToast={showToast}
          platForm={platForm}
        />
        <div className={signUpContainerClasses}>
          <span>Don’t have an account?</span>
          <span className={signUpClasses} onClick={handleSignUpClick}>
            Sign Up
          </span>
        </div>
      </CSSFlex>
    </>
  );
});
Login.displayName = "Login";
Login.propTypes = propTypes;
export default Login;
